import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatMoney } from 'common'

import { BenefitDetailsResponse, BenefitProductClassResponse, deleteFuturePerk } from 'core'
import { EligibleExpenseCategory } from 'core/application/adminDashboard/benefitDetails/design/perks/productClass/getProductClassDesignSummary'
import { frequencies } from 'core/domain/benefit/allowanceFrequency'

import { Button, DeleteConfirmModal, Dropdown, Icon, Menu, MenuItem, Popover, Table, Tabs } from 'common/components'
import { now, nowV2, parseZone, parseZoneFormat } from 'common/dateFormatters'

import { useCommandRequestV2 } from '../../../../common/hooks'

type ActiveAndPastPerksHistoryTabs = {
  eligibleExpenseCategories: EligibleExpenseCategory[]
  offerSameAllowance: boolean
  loadingBenefitDetails: boolean
  activePlanPeriod: BenefitDetailsResponse
  displayActionColumn: boolean
  item: BenefitProductClassResponse
  expenseCategoryId: number
  productClassId: string
  onOpenEndPerkModalClick?: (expenseCategoryId: number, date: string) => void
  onOpenDeleteFuturePerkModalClick?: (expenseCategoryId: number) => void
  onDeleteFuturePerk?: () => void
}
const ActiveAndPastPerksHistoryTabs = ({
  eligibleExpenseCategories,
  offerSameAllowance,
  loadingBenefitDetails,
  activePlanPeriod,
  displayActionColumn,
  item,
  expenseCategoryId,
  productClassId,
  onOpenEndPerkModalClick,
  onOpenDeleteFuturePerkModalClick,
  onDeleteFuturePerk,
}: ActiveAndPastPerksHistoryTabs) => {
  const [tabActiveKey, setTabActiveKey] = useState('active-perks' ?? 'past-perk-history')
  const pastPerks = eligibleExpenseCategories.filter((el) => nowV2().isAfter(el.endDate))
  const activePerks = eligibleExpenseCategories.filter((el) => nowV2().isSameOrBefore(el.endDate))
  const { t: translate } = useTranslation()
  const [showDeletePerkModal, setShowDeleteModal] = useState<boolean>(false)

  const { sendCommand: sendDeleteFuturePerks, isLoading: isCommandLoading } = useCommandRequestV2(
    (perkCategoryId: number) => deleteFuturePerk({ perkCategoryId: perkCategoryId, productClassId: productClassId }),
  )
  const activePerkRow = {
    ...activePerks[0],
    expenseCategoryName: activePerks.map((x) => x.expenseCategoryName).join(', '),
    eligibleExpenseTypes: activePerks.map((x) => ({ name: x.eligibleExpenseTypes.map((y) => y.name).join(' ') })),
  }

  const pastPerkRow = {
    ...pastPerks[0],
    expenseCategoryName: pastPerks.map((x) => x.expenseCategoryName).join(', '),
    eligibleExpenseTypes: pastPerks.map((x) => ({ name: x.eligibleExpenseTypes.map((y) => y.name).join(' ') })),
  }

  const pastPerksConcatenatedList =
    offerSameAllowance && pastPerkRow.eligibleExpenseTypes.length > 0
      ? [pastPerkRow]
      : !offerSameAllowance
      ? pastPerks
      : []

  const activePerksConcatenatedList =
    offerSameAllowance && activePerkRow.eligibleExpenseTypes.length > 0
      ? [activePerkRow]
      : !offerSameAllowance
      ? activePerks
      : []

  const deletePerkThatStartsInTheFuture = async (perkCategoryId: number, productClassId: string) => {
    var result = await sendDeleteFuturePerks(perkCategoryId)
    if (result.hasErrors) return
    setShowDeleteModal(false)
    onDeleteFuturePerk()
  }
  const productClassColumns = (item: any): any[] => {
    const columns = [
      {
        title: translate('admin-dashboard.benefit-details.table.expense-category-name'),
        dataIndex: 'expenseCategoryName',
        key: 'expenseCategoryName',
        sorter: false,
      },

      {
        title: translate('admin-dashboard.benefit-details.table.allowance'),
        dataIndex: 'allowance',
        key: 'allowance',
        sorter: false,
        render: (allowance: number) => {
          return <>{formatMoney(allowance)}</>
        },
      },

      {
        title: translate('admin-dashboard.benefit-details.table.allowance-frequency'),
        dataIndex: 'allowanceFrequency',
        key: 'allowanceFrequency',
        render: (index: number) => {
          return (
            <div>
              <p> {frequencies[index]?.name} </p>
            </div>
          )
        },
      },
      {
        title: translate('admin-dashboard.benefit-details.table.eligible-expense-types'),
        dataIndex: 'eligibleExpenseTypes',
        key: 'eligibleExpenseTypes',
        sorter: false,
        render: (text: string, record: any) => {
          let array = record.eligibleExpenseTypes
          let categories = ''
          array.forEach((el) => {
            categories += el.name + ' '
          })

          return (
            <>
              <p>{getSubCategoriesContent(categories)} </p>
            </>
          )
        },
      },
      {
        title: translate('admin-dashboard.benefit-details.table.start-date'),
        dataIndex: 'startDate',
        key: 'startDate',
        sorter: false,
        render: (text: string, record: any) => {
          return (
            <span>
              <div>{parseZoneFormat(record.startDate)}</div>
            </span>
          )
        },
      },
      {
        title: translate('admin-dashboard.benefit-details.table.end-date'),
        dataIndex: 'endDate',
        key: 'endDate',
        sorter: false,
        render: (text: string, record: any) => {
          return (
            <span>
              <div>
                {!record.isPerkContinuous || !activePlanPeriod.isContinuous
                  ? parseZoneFormat(record.endDate)
                  : 'Continuous'}
              </div>
            </span>
          )
        },
      },
    ]

    let actionsCol = {
      title: translate('admin-dashboard.benefit-details.table.actions'),
      dataIndex: 'action',
      key: 'Action',
      sorter: false,
      render: (text: string, record: any) => {
        console.log(record.startDate)
        console.log('today ', now())
        return (
          <>
            <>
              <Dropdown
                overlay={
                  <Menu>
                    <MenuItem key={1}>
                      <div
                        className="flex ai-center p-4"
                        onClick={() => {
                          const date = !record.isPerkContinuous ? record.endDate : null
                          onOpenEndPerkModalClick(record.expenseCategoryId, date)
                        }}
                      >
                        <Icon type="fas fa-pencil" className="mr-8 mt-4" />
                        Change Perk End Date
                      </div>
                    </MenuItem>
                    {!offerSameAllowance && parseZone(record.startDate).isSameOrAfter(now()) && (
                      <MenuItem key={2}>
                        <div
                          className="flex ai-center p-4"
                          onClick={() => {
                            setShowDeleteModal(true)
                            onOpenDeleteFuturePerkModalClick(record.expenseCategoryId)
                          }}
                        >
                          <Icon type="fas fa-can" className="color-red-6 fa mr-8 fa-trash pointer" />
                          Delete
                        </div>
                      </MenuItem>
                    )}
                  </Menu>
                }
              >
                <Button>...</Button>
              </Dropdown>
            </>
          </>
        )
      },
    }
    if (!displayActionColumn && tabActiveKey === 'active-perks') {
      columns.push(actionsCol)
    }
    return columns
  }

  const getSubCategoriesContent = (notes: any): ReactNode => {
    const icon = (
      <div>
        <span className={'color-green-7'}>See all</span>
      </div>
    )

    return (
      <Popover title={'Sub Categories'} placement="top" content={<div className="popover_width">{notes}</div>}>
        {' '}
        {icon}
      </Popover>
    )
  }

  return (
    <>
      <Tabs
        activeKey={tabActiveKey}
        onTabClick={(tabKey) => {
          setTabActiveKey(tabKey)
        }}
        panes={[
          {
            key: 'active-perks',
            title: <h6>Active Perks </h6>,
            content: (
              <Table
                dataSource={activePerksConcatenatedList}
                loading={loadingBenefitDetails}
                columns={productClassColumns(item)}
                pagination={false}
                scroll={{ x: 'auto' }}
              />
            ),
          },

          {
            key: 'past-perk-history',
            title: <h6>Past Perks History</h6>,
            content: (
              <Table
                dataSource={pastPerksConcatenatedList}
                loading={loadingBenefitDetails}
                columns={productClassColumns(item)}
                pagination={false}
                scroll={{ x: 'auto' }}
              />
            ),
          },
        ]}
      />

      <DeleteConfirmModal
        title={`Deleting `}
        content={'Are you sure you want to delete this perk? This action cannot be undone.'}
        onDelete={async () => deletePerkThatStartsInTheFuture(expenseCategoryId, productClassId)}
        onCancel={() => {
          setShowDeleteModal(false)
        }}
        deleteButtonLoading={isCommandLoading}
        visible={!!showDeletePerkModal}
      />
    </>
  )
}

export default ActiveAndPastPerksHistoryTabs
